import { formatCurrency, formatInteger } from '@/utils/numberFormat';
import { requireReportsSalesTextValue } from '@/composables/reports/sales/useReportsSalesPresentation';

export const SALES_PROFITABILITY_CHART_DEFAULT_METRIC = 'income_vs_expenses';

const SALES_PROFITABILITY_KPI_VISUALS = [
  { icon: 'fa-briefcase', iconBg: 'rgba(0,112,205,0.14)', iconColor: '#0070cd' },
  { icon: 'fa-line-chart', iconBg: 'rgba(59,212,46,0.14)', iconColor: '#1f9b43' },
  { icon: 'fa-credit-card', iconBg: 'rgba(14,116,144,0.15)', iconColor: '#0e7490' },
  { icon: 'fa-thermometer-three-quarters', iconBg: 'rgba(239,68,68,0.14)', iconColor: '#b91c1c' },
  { icon: 'fa-area-chart', iconBg: 'rgba(59, 212, 46, 0.12)', iconColor: '#3bd42e' },
];

export const resolveSalesProfitabilityChartVariantOptions = (reportsSalesText) => {
  const variants = Array.isArray(reportsSalesText.chart.variants)
    ? reportsSalesText.chart.variants
    : [];

  if (variants.length === 0) {
    throw new Error('Missing reports sales chart variants text configuration.');
  }

  return variants.map((line, index) => {
    return {
      value: requireReportsSalesTextValue(line?.value, `reports.sales.chart.variants.${index}.value`),
      label: requireReportsSalesTextValue(line?.label, `reports.sales.chart.variants.${index}.label`),
    };
  });
};

export const normalizeSalesProfitabilityChartPayload = (rawChart = {}, variantOptions = []) => {
  const sourceChart = rawChart && typeof rawChart === 'object' ? rawChart : {};

  let options = (Array.isArray(sourceChart?.variant_options) ? sourceChart.variant_options : [])
    .map((option) => ({
      value: String(option?.value || '').trim(),
      label: String(option?.label || '').trim(),
    }))
    .filter((option) => option.value !== '' && option.label !== '');

  const sourceVariants = sourceChart?.variants && typeof sourceChart.variants === 'object'
    ? sourceChart.variants
    : {};
  const normalizedVariants = {};

  Object.entries(sourceVariants).forEach(([variantCode, variantPayload]) => {
    const code = String(variantCode || '').trim();
    if (code === '') {
      return;
    }

    const categories = (Array.isArray(variantPayload?.categories) ? variantPayload.categories : [])
      .map((category, index) => {
        const value = String(category || '').trim();
        return value !== '' ? value : `P${index + 1}`;
      });

    const series = (Array.isArray(variantPayload?.series) ? variantPayload.series : [])
      .map((seriesItem, index) => ({
        name: String(seriesItem?.name || '').trim() || `Serie ${index + 1}`,
        data: (Array.isArray(seriesItem?.data) ? seriesItem.data : []).map((value) => Number(value || 0)),
      }))
      .filter((seriesItem) => seriesItem.data.length > 0);

    normalizedVariants[code] = {
      categories,
      series,
    };
  });

  if (options.length === 0) {
    options = Object.keys(normalizedVariants)
      .map((code) => ({ value: code, label: code }));
  }

  if (options.length === 0) {
    options = variantOptions;
  }

  return {
    variantOptions: options,
    variants: normalizedVariants,
  };
};

export const normalizeSalesProfitabilityPivot = (value) => {
  const normalized = String(value || '').trim().toLowerCase();

  if ([
    'negocio',
    'cliente_negocio',
    'responsable_negocio',
    'vendedor_negocio',
    'servicio_negocio',
  ].includes(normalized)) {
    return normalized;
  }

  return 'negocio';
};

export const normalizeSalesProfitabilityView = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'total' ? 'total' : 'subtotal';
};

export const buildSalesProfitabilityKpis = (summary = {}, view = 'subtotal', kpiText = {}) => {
  const isTotalView = normalizeSalesProfitabilityView(view) === 'total';
  const source = summary && typeof summary === 'object' ? summary : {};

  const businessAmount = Number(source.business_amount ?? 0);
  const totalBusinesses = Number(source.total_businesses ?? 0);
  const incomesTotal = Number(isTotalView ? (source.incomes_total ?? 0) : (source.incomes_total_subtotal ?? 0));
  const incomesPaid = Number(isTotalView ? (source.incomes_paid ?? 0) : (source.incomes_paid_subtotal ?? 0));
  const expensesTotal = Number(isTotalView ? (source.total_cost_total ?? 0) : (source.total_cost_total_subtotal ?? 0));
  const expensesPaid = Number(isTotalView ? (source.total_cost_paid ?? 0) : (source.total_cost_paid_subtotal ?? 0));
  const budgetTotal = Number(source.budget_total ?? 0);
  const utilityTotal = Number(isTotalView ? (source.utility_total ?? 0) : (source.utility_subtotal ?? 0));
  const marginForecast = Number(isTotalView ? (source.margin_forecast ?? 0) : (source.margin_subtotal ?? 0));
  const utilityReal = Number(isTotalView ? (source.utility_real ?? 0) : (source.utility_subtotal_real ?? 0));
  const marginReal = Number(isTotalView ? (source.margin_real ?? 0) : (source.margin_subtotal_real ?? 0));
  const expenseProgressTotal = Number(isTotalView ? (source.total_cost_progress_total ?? 0) : (source.total_cost_progress_total_subtotal ?? 0));
  const expenseProgressPaid = Number(isTotalView ? (source.total_cost_progress_paid ?? 0) : (source.total_cost_progress_paid_subtotal ?? 0));

  const cards = [
    {
      title: requireReportsSalesTextValue(kpiText.businessesTitle, 'reports.sales.profitability.kpis.businessesTitle'),
      value: formatCurrency(businessAmount, { currency: 'MXN' }),
      valueMeta: `${formatInteger(totalBusinesses)} ${requireReportsSalesTextValue(kpiText.businessesSuffix, 'reports.sales.profitability.kpis.businessesSuffix')}`,
    },
    {
      title: requireReportsSalesTextValue(kpiText.incomesTitle, 'reports.sales.profitability.kpis.incomesTitle'),
      value: formatCurrency(incomesTotal, { currency: 'MXN' }),
      valueMeta: `${requireReportsSalesTextValue(kpiText.incomesCollectedPrefix, 'reports.sales.profitability.kpis.incomesCollectedPrefix')} ${formatCurrency(incomesPaid, { currency: 'MXN' })}`,
    },
    {
      title: requireReportsSalesTextValue(kpiText.expensesTitle, 'reports.sales.profitability.kpis.expensesTitle'),
      value: formatCurrency(expensesTotal, { currency: 'MXN' }),
      valueMeta: `${requireReportsSalesTextValue(kpiText.expensesPaidPrefix, 'reports.sales.profitability.kpis.expensesPaidPrefix')} ${formatCurrency(expensesPaid, { currency: 'MXN' })}`,
    },
    {
      title: requireReportsSalesTextValue(kpiText.budgetTitle, 'reports.sales.profitability.kpis.budgetTitle'),
      value: formatCurrency(budgetTotal, { currency: 'MXN' }),
      valueMeta: requireReportsSalesTextValue(kpiText.budgetProgressLabel, 'reports.sales.profitability.kpis.budgetProgressLabel'),
      valueMetaItems: [
        {
          label: requireReportsSalesTextValue(kpiText.totalForecastLabel, 'reports.sales.profitability.kpis.totalForecastLabel'),
          value: `${expenseProgressTotal.toFixed(2)}%`,
          tone: 'base',
        },
        {
          label: requireReportsSalesTextValue(kpiText.paidLabel, 'reports.sales.profitability.kpis.paidLabel'),
          value: `${expenseProgressPaid.toFixed(2)}%`,
          tone: 'base',
        },
      ],
    },
    {
      title: requireReportsSalesTextValue(kpiText.utilityTitle, 'reports.sales.profitability.kpis.utilityTitle'),
      value: formatCurrency(utilityTotal, { currency: 'MXN' }),
      valueMetaItems: [
        {
          label: requireReportsSalesTextValue(kpiText.marginForecastLabel, 'reports.sales.profitability.kpis.marginForecastLabel'),
          value: `${marginForecast.toFixed(2)}%`,
          tone: 'base',
        },
        {
          label: requireReportsSalesTextValue(kpiText.utilityRealLabel, 'reports.sales.profitability.kpis.utilityRealLabel'),
          value: formatCurrency(utilityReal, { currency: 'MXN' }),
          tone: 'base',
        },
        {
          label: requireReportsSalesTextValue(kpiText.marginRealLabel, 'reports.sales.profitability.kpis.marginRealLabel'),
          value: `${marginReal.toFixed(2)}%`,
          tone: 'base',
        },
      ],
    },
  ];

  return cards.map((card, index) => ({
    ...card,
    ...SALES_PROFITABILITY_KPI_VISUALS[index % SALES_PROFITABILITY_KPI_VISUALS.length],
  }));
};
